// ANGEL FLIGHT — GunView3D: the foreground first-person gun + cockpit viewmodel.
//
// This is the FPS "hero" the player reads as "the minigun in my hands, leaning
// out the door". It lives in its OWN scene + camera, rendered as an overlay pass
// AFTER the world (the lead does renderer.autoClear=false + clearDepth between
// passes, then calls renderOverlay) so the gun NEVER clips the world geometry.
//
// Feel = the point. Every frame the rig:
//   • SWINGS toward the player's aim (yaw from aim.x, pitch from aim.y),
//   • IDLE-BOBS a touch (engine vibration),
//   • RECOILS via a 1-D damped spring kicked on each shot (back + muzzle climb),
//   • SPINS its barrel cluster, ramping angular velocity with gunner.spinup,
//   • HEATS its barrels under sustained fire — the cluster glows hot orange via
//     an emissive ramp driven by a heat accumulator, cooling when fire stops.
//
// The gun is the hero asset, so its surface is REAL scratched-metal PBR (ambientCG
// Metal032: color + normal + roughness + metalness) tinted to oiled gunmetal, lit
// by the overlay's own lights + a tiny procedural specular env so the worn metal
// catches a moving highlight as it sways.
//
// CRITICAL: muzzleScreenAnchor() exposes (approximately) WHERE the barrel tip
// renders on screen this frame (NDC), so the lead spawns world tracers from the
// LIVE barrel — not a frozen point. getMuzzleViewOffset() exposes the muzzle tip
// in gunCam local space for callers that want the raw 3D point.
//
// All tunables come from RENDER.gun (config/render.ts). No magic numbers here.
// No per-frame allocations in update() — temp vectors are cached on the instance.

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { RENDER } from '../config/render';
import { BALANCE } from '../config/balance';
import { damp } from '../core/mathx';
import type { GunnerRef } from '../systems/System';

const G = RENDER.gun;

/** Where the muzzle tip lives in the gun rig's LOCAL space (the front of the
 *  barrel cluster). The cluster is built pointing down -Z, length ~`barrelLen`. */
const BARREL_LEN = 1.15;
const MUZZLE_LOCAL = new THREE.Vector3(0, 0, -BARREL_LEN - 0.06);

/** White-hot target the barrel emissive lerps toward as heat climbs (so the glow
 *  reads as a temperature ramp: deep red → orange → near-white). Module-level
 *  const = no per-frame allocation. */
const THREE_WHITE = new THREE.Color(0xffffff);

export class GunView3D {
  /** The overlay scene + camera the gun is rendered with (exposed for the lead). */
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;

  // Rig hierarchy:
  //   rig (positioned at rest, swung toward aim, bobbed, recoiled)
  //     └─ body  (receiver/housing + mount)
  //     └─ barrels (the spinnable cylinder ring — spun about its local Z)
  private readonly rig = new THREE.Group();
  private readonly barrels = new THREE.Group();
  private readonly muzzleTip = new THREE.Object3D(); // child marker at the barrel tip

  // Swing/sway state (eased toward the aim-derived targets via damp()).
  private yaw = 0;
  private pitch = 0;

  // Idle bob phase.
  private bobT = 0;

  // 1-D recoil spring (x = compression along the recoil axis; >0 = pushed back).
  private recoilX = 0;
  private recoilVel = 0;

  // Barrel spin state.
  private barrelAngle = 0;

  /** Vertical offset added to the rest pose (intro cinematic: starts at
   *  CINEMATIC.gunDropY so the gun sits below the frame, eased to 0 as the
   *  minigun "rises into the door"). 0 in normal play. */
  riseOffset = 0;

  // Barrel HEAT accumulator (0..1): rises while firing, decays when not. Drives
  // the barrel material's emissive so the cluster glows hot under sustained fire.
  private heat = 0;
  private barrelMat!: THREE.MeshStandardMaterial; // the barrels' material (emissive driven)
  private readonly heatColor = new THREE.Color(G.barrelHeatColor);

  // Disposal bookkeeping.
  private readonly geometries: THREE.BufferGeometry[] = [];
  private readonly materials: THREE.Material[] = [];
  private readonly textures: THREE.Texture[] = [];
  private gltf: { dispose?: () => void } | null = null;
  private disposed = false;

  // Cached temps — NO per-frame allocation.
  private readonly tmpWorld = new THREE.Vector3();
  private readonly tmpNdc = new THREE.Vector3();

  constructor() {
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(G.fovDeg, 1, 0.01, 50);
    // The overlay camera sits at the origin looking down -Z; the rig is placed in
    // front of it at rest. (Aspect is set in resize().)
    this.camera.position.set(0, 0, 0);
    this.camera.lookAt(0, 0, -1);
  }

  /** Build the gun + cockpit framing + lights. Call once before update(). */
  init(): void {
    this.buildEnvironment();
    this.buildLights();
    this.buildGun();
    this.buildCockpitFrame();

    // The muzzle marker is a child of the BARRELS so it inherits spin pose; its
    // local position is the barrel tip. World position is read each frame.
    this.muzzleTip.position.copy(MUZZLE_LOCAL);
    this.barrels.add(this.muzzleTip);

    this.rig.position.set(G.restX, G.restY, G.restZ);
    this.scene.add(this.rig);

    // NOTE: the Kenney blaster GLB is a green sci-fi pistol — wrong shape + clashes
    // with the gunmetal minigun look. The hand-built primitive minigun (dark
    // barrel cluster + receiver) reads better as a mounted door gun, so we ship
    // that. tryLoadGlb() is kept for a future proper minigun model.
    void this.tryLoadGlb;
  }

  /** Keep the overlay camera aspect matched to the canvas. */
  resize(cssWidth: number, cssHeight: number): void {
    this.camera.aspect = Math.max(1, cssWidth) / Math.max(1, cssHeight);
    this.camera.updateProjectionMatrix();
  }

  // ── build helpers ───────────────────────────────────────────────────────────

  private track<T extends THREE.BufferGeometry | THREE.Material | THREE.Texture>(x: T): T {
    if (x instanceof THREE.BufferGeometry) this.geometries.push(x);
    else if (x instanceof THREE.Texture) this.textures.push(x);
    else this.materials.push(x);
    return x;
  }

  /** A tiny procedural specular environment so the scratched metal catches a
   *  moving highlight as the gun sways — bright cool sky overhead, dark ground
   *  below, a hot warm rim near the horizon (matches the dusk world grade). Built
   *  from a small gradient CanvasTexture used as an equirectangular reflection
   *  map. Cheap (no PMREM/renderer needed); the lights still carry the lighting,
   *  this is just a faint reflection so flat metal doesn't read as dead matte. */
  private buildEnvironment(): void {
    const c = document.createElement('canvas');
    c.width = 16;
    c.height = 64;
    const g2d = c.getContext('2d');
    if (!g2d) return;
    const grad = g2d.createLinearGradient(0, 0, 0, 64);
    grad.addColorStop(0.0, '#cfd6e0'); // cool bright sky (zenith)
    grad.addColorStop(0.45, '#aeb6c2'); // sky → horizon
    grad.addColorStop(0.55, '#ffac63'); // warm dusk rim at the horizon
    grad.addColorStop(0.7, '#3a2a20'); // warm dark just below horizon
    grad.addColorStop(1.0, '#0a0b0e'); // near-black ground
    g2d.fillStyle = grad;
    g2d.fillRect(0, 0, 16, 64);

    const tex = this.track(new THREE.CanvasTexture(c));
    tex.mapping = THREE.EquirectangularReflectionMapping;
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
    // Used directly as scene.environment — MeshStandardMaterial samples it for
    // specular IBL (no roughness prefilter, but it's only a faint catch here).
    this.scene.environment = tex;
  }

  private buildLights(): void {
    // The overlay scene needs its own lights (it is NOT the world scene). A warm
    // key + cool fill reads the gunmetal nicely and matches the dusk world grade.
    const key = new THREE.DirectionalLight(0xffe6c0, 2.4);
    key.position.set(0.6, 1.0, 0.4);
    const fill = new THREE.HemisphereLight(0x6a7a9a, 0x14181f, 0.9);
    this.scene.add(key, fill);
  }

  /** Load the scratched-metal PBR set (ambientCG Metal032) and return a fresh,
   *  tiled Texture quartet. Each call makes its OWN Texture instances so parts
   *  can tile independently; all are tracked for disposal. Mirrors ColorWorld's
   *  loader pattern (SRGB on color only, RepeatWrapping, anisotropy). */
  private loadGunmetalMaps(repeat: number): {
    map: THREE.Texture;
    normalMap: THREE.Texture;
    roughnessMap: THREE.Texture;
    metalnessMap: THREE.Texture;
  } {
    const loader = new THREE.TextureLoader();
    const base = import.meta.env.BASE_URL + 'assets/textures/';

    const map = this.track(loader.load(base + 'gunmetal_color.jpg'));
    map.colorSpace = THREE.SRGBColorSpace;
    const normalMap = this.track(loader.load(base + 'gunmetal_normal.jpg'));
    const roughnessMap = this.track(loader.load(base + 'gunmetal_roughness.jpg'));
    const metalnessMap = this.track(loader.load(base + 'gunmetal_metalness.jpg'));

    for (const tex of [map, normalMap, roughnessMap, metalnessMap]) {
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(repeat, repeat);
      tex.anisotropy = 8;
    }
    return { map, normalMap, roughnessMap, metalnessMap };
  }

  private buildGun(): void {
    // Receiver / body material: scratched gunmetal PBR with a cool steel tint.
    const bodyMaps = this.loadGunmetalMaps(G.metalRepeat);
    const gunmetal = this.track(
      new THREE.MeshStandardMaterial({
        color: new THREE.Color(G.metalTint),
        map: bodyMaps.map,
        normalMap: bodyMaps.normalMap,
        roughnessMap: bodyMaps.roughnessMap,
        metalnessMap: bodyMaps.metalnessMap,
        metalness: G.metalness,
        roughness: G.roughness,
        normalScale: new THREE.Vector2(G.normalScale, G.normalScale),
        envMapIntensity: G.envIntensity,
      }),
    );
    // Mount/cap: same scratched maps but darker + rougher (oiled, in shadow).
    const darkMaps = this.loadGunmetalMaps(G.metalRepeat * 1.3);
    const darkMetal = this.track(
      new THREE.MeshStandardMaterial({
        color: new THREE.Color(G.metalTint).multiplyScalar(0.45),
        map: darkMaps.map,
        normalMap: darkMaps.normalMap,
        roughnessMap: darkMaps.roughnessMap,
        metalnessMap: darkMaps.metalnessMap,
        metalness: G.metalness * 0.8,
        roughness: Math.min(1, G.roughness + 0.25),
        normalScale: new THREE.Vector2(G.normalScale, G.normalScale),
        envMapIntensity: G.envIntensity,
      }),
    );
    // The spinning barrels get their OWN material (independent tile + emissive
    // heat). emissive starts black; update() ramps emissiveIntensity with heat.
    const barrelMaps = this.loadGunmetalMaps(G.metalRepeat * 0.7);
    const barrelMat = this.track(
      new THREE.MeshStandardMaterial({
        color: new THREE.Color(G.metalTint).multiplyScalar(0.85),
        map: barrelMaps.map,
        normalMap: barrelMaps.normalMap,
        roughnessMap: barrelMaps.roughnessMap,
        metalnessMap: barrelMaps.metalnessMap,
        metalness: G.metalness,
        roughness: G.roughness,
        normalScale: new THREE.Vector2(G.normalScale, G.normalScale),
        envMapIntensity: G.envIntensity,
        emissive: new THREE.Color(G.barrelHeatColor),
        // Muzzle-weighted gradient: the glow is hottest at the muzzle end and
        // fades toward the receiver, so a hot cluster reads as heated barrels
        // rather than a flat uniform fill (the G2 "pink hand" defect).
        emissiveMap: this.buildHeatGradient(),
        emissiveIntensity: 0, // ramped by heat in update()
      }),
    );
    this.barrelMat = barrelMat;

    // Receiver / housing — a chunky box the barrels emerge from.
    const receiverGeo = this.track(new THREE.BoxGeometry(0.42, 0.42, 0.7));
    const receiver = new THREE.Mesh(receiverGeo, gunmetal);
    receiver.position.set(0, 0, 0.1);
    this.rig.add(receiver);

    // A small mount/yoke below the receiver — sells "pintle-mounted, leaning out".
    const mountGeo = this.track(new THREE.BoxGeometry(0.16, 0.5, 0.16));
    const mount = new THREE.Mesh(mountGeo, darkMetal);
    mount.position.set(0, -0.38, 0.18);
    this.rig.add(mount);

    // Rear cap on the barrel cluster (the rotor face).
    const capGeo = this.track(new THREE.CylinderGeometry(0.2, 0.2, 0.12, 12));
    const cap = new THREE.Mesh(capGeo, darkMetal);
    cap.rotation.x = Math.PI / 2; // align cylinder axis to Z
    cap.position.set(0, 0, -0.22);
    this.barrels.add(cap);

    // The spinning barrel ring: 6 thin cylinders arranged in a circle, all pointing
    // down -Z. CylinderGeometry is Y-up by default → rotate each to lie along Z.
    const BARRELS = 6;
    const RING_R = 0.12;
    const barrelGeo = this.track(new THREE.CylinderGeometry(0.035, 0.035, BARREL_LEN, 8));
    for (let i = 0; i < BARRELS; i++) {
      const a = (i / BARRELS) * Math.PI * 2;
      const b = new THREE.Mesh(barrelGeo, barrelMat);
      b.rotation.x = Math.PI / 2; // Y-axis → Z-axis
      b.position.set(Math.cos(a) * RING_R, Math.sin(a) * RING_R, -BARREL_LEN / 2 - 0.22);
      this.barrels.add(b);
    }

    // The barrel cluster origin sits just forward of the receiver.
    this.barrels.position.set(0, 0, 0);
    this.rig.add(this.barrels);

    // W3 mechanical detail (static, from G.detail): a barrel SHROUD the cluster
    // emerges from, a top FRONT-SIGHT post, and a side AMMO CAN — so the gun
    // reads as a real belt-fed rotary cannon instead of a box + bare cylinders.
    const DT = G.detail;
    const shroudGeo = this.track(
      new THREE.CylinderGeometry(DT.shroudRadius, DT.shroudRadius, DT.shroudLen, 14, 1, true),
    );
    const shroud = new THREE.Mesh(shroudGeo, darkMetal);
    shroud.rotation.x = Math.PI / 2; // cylinder axis Y → Z
    shroud.position.set(0, 0, DT.shroudZ);
    this.rig.add(shroud);

    const sightGeo = this.track(new THREE.BoxGeometry(DT.sightW, DT.sightH, DT.sightW));
    const sight = new THREE.Mesh(sightGeo, darkMetal);
    sight.position.set(0, DT.sightY, DT.sightZ);
    this.rig.add(sight);

    const ammoGeo = this.track(new THREE.BoxGeometry(DT.ammoW, DT.ammoH, DT.ammoD));
    const ammo = new THREE.Mesh(ammoGeo, gunmetal);
    ammo.position.set(DT.ammoX, DT.ammoY, DT.ammoZ);
    this.rig.add(ammo);
  }

  /** A 1×64 vertical gradient used as the barrels' emissiveMap. CylinderGeometry
   *  v runs along the barrel length; after the rig's rotation the v=0 end is the
   *  MUZZLE. With flipY the canvas bottom row maps to v=0, so the gradient is
   *  bright at the bottom (muzzle, full glow) fading dark toward the top
   *  (receiver end, ~15% glow). */
  private buildHeatGradient(): THREE.Texture {
    const c = document.createElement('canvas');
    c.width = 1;
    c.height = 64;
    const g2d = c.getContext('2d');
    if (g2d) {
      const grad = g2d.createLinearGradient(0, 0, 0, 64);
      grad.addColorStop(0.0, '#262626'); // receiver end — faint residual heat
      grad.addColorStop(0.55, '#8c8c8c');
      grad.addColorStop(1.0, '#ffffff'); // muzzle — full glow
      g2d.fillStyle = grad;
      g2d.fillRect(0, 0, 1, 64);
    }
    const tex = this.track(new THREE.CanvasTexture(c));
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
    return tex;
  }

  private buildCockpitFrame(): void {
    // A REAL door OPENING (G2 reframe): thin pillars hugging the side edges, a
    // shallow header bar, and a thicker sill (the cabin floor edge). The old
    // two floating corner slabs ate 20-35% of the frame; this frames the world
    // through a doorway instead of blocking it. All sizes from RENDER.gun.
    const D = G.doorframe;
    const frameMat = this.track(
      new THREE.MeshStandardMaterial({
        color: D.color,
        metalness: 0.4,
        roughness: 0.85,
      }),
    );
    const pillarGeo = this.track(new THREE.BoxGeometry(D.pillarW, D.pillarH, D.frameDepth));
    const headerGeo = this.track(new THREE.BoxGeometry(D.width, D.headerH, D.frameDepth));
    const sillGeo = this.track(new THREE.BoxGeometry(D.width, D.sillH, D.frameDepth));

    const left = new THREE.Mesh(pillarGeo, frameMat);
    left.position.set(-D.pillarX, 0, D.depthZ);
    this.scene.add(left);

    const right = new THREE.Mesh(pillarGeo, frameMat);
    right.position.set(D.pillarX, 0, D.depthZ);
    this.scene.add(right);

    const header = new THREE.Mesh(headerGeo, frameMat);
    header.position.set(0, D.headerY, D.depthZ);
    this.scene.add(header);

    const sill = new THREE.Mesh(sillGeo, frameMat);
    sill.position.set(0, D.sillY, D.depthZ);
    this.scene.add(sill);
  }

  /** Best-effort swap of the CC0 GLB body. Non-blocking; failure is silent (the
   *  primitive gun stays). The GLB references a sibling Textures/colormap.png by
   *  relative URI, so loading from its folder lets GLTFLoader resolve it. */
  private tryLoadGlb(): void {
    const url = `${import.meta.env.BASE_URL}assets/models/blaster-kit/blaster-p.glb`;
    const loader = new GLTFLoader();
    loader.load(
      url,
      (gltf) => {
        if (this.disposed) return;
        const model = gltf.scene;
        model.scale.setScalar(G.modelScale);
        // Orient the blaster to point down -Z (Kenney blasters face +Z/+Y'ish).
        model.rotation.y = Math.PI;
        model.position.set(0, 0.02, 0.18);
        // Add as a sibling to the primitive body so it dresses the receiver; keep
        // the primitive barrel cluster as the spinning part (the GLB has none).
        this.rig.add(model);
        this.gltf = {
          dispose: () => {
            model.traverse((o) => {
              const m = o as THREE.Mesh;
              if (m.isMesh) {
                m.geometry?.dispose();
                const mat = m.material;
                if (Array.isArray(mat)) mat.forEach((x) => this.disposeMat(x));
                else if (mat) this.disposeMat(mat);
              }
            });
          },
        };
      },
      undefined,
      () => {
        // Load failed (offline/missing/CORS) — keep the primitive gun. No throw.
      },
    );
  }

  private disposeMat(m: THREE.Material): void {
    const anyMat = m as unknown as Record<string, unknown>;
    for (const key of ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'emissiveMap']) {
      const t = anyMat[key];
      if (t instanceof THREE.Texture) t.dispose();
    }
    m.dispose();
  }

  // ── per-frame ────────────────────────────────────────────────────────────────

  /** Kicked on the `weapon:fired` bus event — one recoil impulse. `scale` is the
   *  weapon's recoil multiplier (heavy guns kick harder). */
  kick(scale = 1): void {
    this.recoilVel += G.recoilKick * scale;
  }

  /**
   * Advance the viewmodel. `dt` is SECONDS. Reads the gunner to drive swing
   * (aim), spin (spinup) — recoil is driven separately by kick().
   */
  update(dt: number, gunner: GunnerRef): void {
    // 1) SWING toward aim. Convert aim (view px, 0..1280 / 0..720) to a centered
    //    -1..1 offset, then to small yaw/pitch targets scaled by aimFollow.
    const ax = (gunner.aim.x / BALANCE.VIEW.width) * 2 - 1; // -1 (left) .. 1 (right)
    const ay = (gunner.aim.y / BALANCE.VIEW.height) * 2 - 1; // -1 (top) .. 1 (bottom)
    const yawTarget = -ax * G.aimFollow; // aim right → barrels swing right (rig yaw -)
    const pitchTarget = ay * G.aimFollow; // aim down → barrels tip down (rig pitch +)
    this.yaw = damp(this.yaw, yawTarget, G.swayLambda, dt);
    this.pitch = damp(this.pitch, pitchTarget, G.swayLambda, dt);

    // 2) IDLE BOB — a small figure-8 (engine vibration). x = sin, y = sin(2x).
    this.bobT += dt;
    const w = this.bobT * G.idleBobHz * Math.PI * 2;
    const bobX = Math.sin(w) * G.idleBobAmp;
    const bobY = Math.sin(w * 2) * G.idleBobAmp * 0.5;

    // 3) RECOIL SPRING (1-D damped harmonic oscillator). recoilX is compression.
    this.recoilVel += (-G.recoilStiffness * this.recoilX - G.recoilDamping * this.recoilVel) * dt;
    this.recoilX += this.recoilVel * dt;

    // 4) BARREL SPIN — angular velocity ramps with spinup, peaking at barrelSpinMax.
    const spin = Math.min(1, Math.max(0, gunner.spinup));
    this.barrelAngle += spin * G.barrelSpinMax * dt;
    this.barrels.rotation.z = this.barrelAngle;

    // 5) BARREL HEAT — accumulate while the gun is actually firing (spinup above
    //    the fire threshold), bleed off otherwise. Heat (0..1) drives the barrel
    //    emissive so the cluster glows hot orange under sustained fire + cools off.
    const firing = spin > G.barrelHeatFireThreshold;
    const heatRate = firing ? G.barrelHeatRise : -G.barrelHeatDecay;
    this.heat = Math.min(1, Math.max(0, this.heat + heatRate * dt));
    // Smoothstep so the glow eases in (cooler barrels barely show, hot ones bloom).
    const glow = this.heat * this.heat * (3 - 2 * this.heat);
    // Bloom-aware cap (G2): the ramp tops out just above the grade's bloom
    // threshold so a hot cluster glows without blooming into a white blob.
    this.barrelMat.emissiveIntensity = Math.min(glow * G.barrelHeatMax, G.barrelHeatBloomCap);
    // Shift the emissive slightly toward yellow-white at peak heat — a hint of a
    // temperature ramp. Kept subtle (0.12) because a strong white shift + ACES
    // grade desaturated the whole cluster into a flat pastel-pink "hand" at full
    // heat (the G2 viewmodel defect); the glow must stay saturated hot metal.
    this.barrelMat.emissive.copy(this.heatColor).lerp(THREE_WHITE, this.heat * 0.12);

    // Compose the rig pose: rest + bob + recoil (back along +Z) on position,
    // swing + recoil muzzle-climb on rotation.
    this.rig.position.set(
      G.restX + bobX,
      G.restY + bobY + this.riseOffset,
      G.restZ + this.recoilX, // +Z = toward camera = "kicked back"
    );
    this.rig.rotation.set(
      this.pitch + this.recoilX * G.recoilPitch, // muzzle climbs up on recoil
      this.yaw,
      0,
    );
  }

  // ── muzzle exposure (the tracer-origin fix) ──────────────────────────────────

  /**
   * The barrel-tip position in gunCam LOCAL space (i.e. relative to the overlay
   * camera, which sits at the origin looking down -Z). Reflects the live swing +
   * recoil + spin pose this frame. Writes into `target` and returns it.
   */
  getMuzzleViewOffset(target: THREE.Vector3): THREE.Vector3 {
    // World position in the gunScene == camera-local position, since gunCam is at
    // the origin with identity rotation.
    this.muzzleTip.updateWorldMatrix(true, false);
    return target.setFromMatrixPosition(this.muzzleTip.matrixWorld);
  }

  /**
   * The barrel-tip WORLD position in the overlay scene. (Identical to
   * getMuzzleViewOffset because gunCam is at the origin — provided for callers
   * that think in "world" terms within the gun scene.) Writes into `target`.
   */
  muzzleWorldPosition(target: THREE.Vector3): THREE.Vector3 {
    return this.getMuzzleViewOffset(target);
  }

  /**
   * Approximate normalized-device-coordinates ({nx, ny} in -1..1, where +y is UP)
   * at which the muzzle tip renders THIS frame. The lead maps this onto the MAIN
   * world camera to spawn tracers from the live barrel instead of a frozen point.
   * Projected through the overlay camera so it tracks the swing + recoil.
   */
  muzzleScreenAnchor(): { nx: number; ny: number } {
    this.getMuzzleViewOffset(this.tmpWorld);
    this.tmpNdc.copy(this.tmpWorld).project(this.camera);
    return { nx: this.tmpNdc.x, ny: this.tmpNdc.y };
  }

  // ── render + lifecycle ───────────────────────────────────────────────────────

  /**
   * Overlay render: clears the DEPTH buffer (so the gun draws on top of the world
   * without z-fighting it) and draws the gun scene with the gun camera. The lead
   * has already set renderer.autoClear=false and drawn the world pass.
   */
  renderOverlay(renderer: THREE.WebGLRenderer): void {
    renderer.clearDepth();
    renderer.render(this.scene, this.camera);
  }

  dispose(): void {
    this.disposed = true;
    this.scene.environment = null; // drop the procedural env ref (tex disposed below)
    this.scene.clear();
    for (const g of this.geometries) g.dispose();
    for (const m of this.materials) m.dispose();
    for (const t of this.textures) t.dispose();
    this.geometries.length = 0;
    this.materials.length = 0;
    this.textures.length = 0;
    this.gltf?.dispose?.();
    this.gltf = null;
  }
}
